/* eslint-disable jsx-a11y/no-static-element-interactions */
/* eslint-disable jsx-a11y/click-events-have-key-events */
import clsx from 'clsx';
import React, {
  type KeyboardEvent,
  type ReactNode,
  useRef,
  useState,
} from 'react';

type TextSizeType = 'sm' | 'md' | 'lg';

export type BaseRowType = {
  id: unknown;
  testId?: string;
};

type ColSpanValues = '1' | '2' | '3' | '4' | '5' | '6' | '7';

type CellProps = {
  justify?: 'left' | 'right';
  colspan?: `col-span-${ColSpanValues}`;
};

type Formatter<K> = (value: K) => ReactNode | null;

export type ColumnDefinition<T> = CellProps & { title: string } & (
    | {
        key: keyof T;
      }
    | { format: Formatter<T> }
  );

const formatKeyAsText = <T,>(data: T) => <span>{String(data)}</span>;

const format = <T,>(value: T, definition: ColumnDefinition<T>) => {
  if ('format' in definition) {
    return definition.format(value);
  }
  return formatKeyAsText(value[definition.key]);
};

type CellComponentProps = {
  justify?: 'left' | 'right';
  colspan?: `col-span-${ColSpanValues}`;
  children: ReactNode;
};

const Cell = ({
  children,
  justify,
  colspan,
}: CellComponentProps): JSX.Element => (
  <div
    role="cell"
    className={clsx(
      justify === 'right'
        ? 'text-right justify-end'
        : 'text-left justify-start',
      colspan,
      'whitespace-nowrap overflow-x-scroll scrollbar-hide items-center flex w-full'
    )}
  >
    {children}
  </div>
);

type RowProps<T> = {
  value: T;
  schema: readonly ColumnDefinition<T>[];
  selected?: boolean;
  index: number;
  isLast?: boolean;
};

const Row = <T,>({
  value,
  schema,
  selected,
  index,
  isLast = false,
}: RowProps<T>) => (
  <div
    className={clsx(
      'grid grid-flow-col auto-cols-fr gap-x-[1ch]',
      'px-f1 py-f1',
      selected ? 'bg-negative' : 'bg-transparent',
      'group-hover:bg-negative group-focus:bg-negative cursor-pointer',
      'border-b-[0.5px] border-main-300',
      isLast && 'border-b-[0px]'
    )}
    role="row"
    aria-rowindex={index}
  >
    {schema.map((def) => (
      <Cell key={def.title} justify={def.justify} colspan={def.colspan}>
        {format(value, def)}
      </Cell>
    ))}
  </div>
);

type HeaderProps<T extends BaseRowType> = {
  schema: readonly ColumnDefinition<T>[];
  textSize?: TextSizeType;
  showHeader?: boolean;
};

const Header = <T extends BaseRowType>({
  schema,
  textSize = 'sm',
  showHeader = true,
}: HeaderProps<T>) => {
  if (!showHeader) return null;

  return (
    <div
      className={clsx(
        'grid grid-flow-col auto-cols-fr gap-x-[1ch]',
        'px-f2 py-f1',
        'border-b-[0.5px] border-main-300'
      )}
      role="row"
    >
      {schema.map(({ justify, colspan, title }) => (
        <div
          key={title}
          className={clsx(
            justify === 'right' ? 'text-right' : 'text-left',
            colspan
          )}
        >
          <div
            className={`text-main-500 ${
              textSize === 'sm' ? 'h-f3' : 'h-f2'
            } flex flex-row items-center`}
            role="columnheader"
          >
            <span
              className={clsx([
                'flex flex-shrink-0',
                justify === 'right' && 'ml-auto',
              ])}
            >
              {title}
            </span>
          </div>
        </div>
      ))}
    </div>
  );
};

export type SubTableProps<T extends BaseRowType> = {
  data: readonly T[];
  schema: readonly ColumnDefinition<T>[];
  onClick: (row: T) => void;
  selected?: Pick<T, 'id'>;
  onRowHover?: (elem: T) => void;
  emptyValue?: Record<string, ReactNode>;
  'data-testid'?: string;
  textSize?: TextSizeType;
  stickyHeader?: boolean;
  showHeader?: boolean;
};

/**
 * A clickable table component with similar interface to NavTableV2
 * @param data - an array of data
 * @param schema - the table schema. See `ColumnDefinition`
 * @param onClick - callback function when a row is clicked
 * @param selected - whether or not the row has been selected
 * @param onRowHover - callback when hovering over a row
 * @param emptyValue - a mapping of row title to string. Rendered when no data is present
 * @param showHeader - whether to show header titles (default: true)
 * @param textSize - text size for the table (default: 'sm')
 * @param stickyHeader - whether the header should stick to the top (default: true)
 */
export const SubTable = <T extends BaseRowType>({
  data,
  schema,
  onClick,
  selected,
  onRowHover,
  emptyValue,
  'data-testid': dataTestId,
  textSize = 'sm',
  stickyHeader = true,
  showHeader = true,
}: SubTableProps<T>) => {
  // In some cases, we will arrive at this table with a member already selected
  // due to deeplinks. If that's the case, pre-set the focus index. Otherwise, set to 0
  const selectedIdx = data.findIndex((row) => row.id === selected?.id);
  const [focusedIdx, setFocusedIdx] = useState<number>(
    selectedIdx < 0 ? 0 : selectedIdx
  );
  const tableRef = useRef<HTMLDivElement>(null);

  return (
    <div
      className="min-h-f0"
      role="table"
      aria-label="Table"
      data-testid={dataTestId}
      ref={tableRef}
    >
      {showHeader && (
        <div
          className={
            stickyHeader ? 'sticky -top-f4 pt-f4 -mt-f4 bg-canvas' : ''
          }
        >
          <Header textSize={textSize} schema={schema} showHeader={showHeader} />
        </div>
      )}
      {/* the table height should be the left overspace after subtracting padding and other */}
      {/* component heights in the main pane */}
      {data.length > 0 && (
        <div
          className={clsx(
            'overflow-auto',
            'h-[calc(100%_-_27px)]',
            'z-0',
            !showHeader && 'border-t-[0.5px]',
            'border-main-300'
          )}
        >
          {data.map((row, idx) => (
            <div
              key={String(row.id)}
              data-testid="sub-table-row"
              className="group outline-none"
              data-idx={String(idx)}
              onClick={() => onClick(row)}
              onMouseOver={onRowHover ? () => onRowHover(row) : undefined}
              onKeyDown={(e: KeyboardEvent<HTMLDivElement>) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  onClick(row);
                } else if (e.key === 'ArrowUp') {
                  e.preventDefault();
                  const index = idx === 0 ? 0 : idx - 1;
                  tableRef.current
                    ?.querySelector<HTMLDivElement>(`div[data-idx="${index}"]`)
                    ?.focus();
                } else if (e.key === 'ArrowDown') {
                  e.preventDefault();
                  const index = idx === data.length ? data.length : idx + 1;
                  tableRef.current
                    ?.querySelector<HTMLDivElement>(`div[data-idx="${index}"]`)
                    ?.focus();
                }
              }}
              onFocus={() => setFocusedIdx(idx)}
              tabIndex={idx === focusedIdx ? 0 : -1}
            >
              <Row
                index={idx}
                value={row}
                selected={selected?.id === row.id}
                schema={schema}
                isLast={idx === data.length - 1}
              />
            </div>
          ))}
        </div>
      )}
      {/* If no rows, optionally display a sentinel row to avoid a weird UX */}
      {/* Use data.length, not !data for array-ish objects (used in stories) */}
      {data.length === 0 && emptyValue && (
        <div className="overflow-auto h-[calc(100%_-_27px)] z-0 pb-f2 border-t-[0.5px] border-main-300">
          <div
            className={clsx(
              'grid grid-flow-col auto-cols-fr gap-x-[1ch]',
              'px-f1 py-f1',
              'bg-transparent',
              'border-b-[0.5px] border-main-300'
            )}
            role="row"
          >
            {schema.map((def) => (
              <Cell key={def.title} justify={def.justify} colspan={def.colspan}>
                {emptyValue[def.title]}
              </Cell>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};
